import { useCallback, useEffect, useRef, useState } from "react";
import { Image, Collapse, Input, Upload, Button, Card } from "antd";
import type { UploadProps, UploadFile } from "antd";
import StarRating from "components/star-rating/star-rating";
import { useAddProductFeedbackMutation } from "graphql/mutations/add-product-feedback.generated";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import DropdownArrow from "components/dropdown-arrow/dropdown-arrow";
import {
	getBase64,
	FileType,
	uploadFeedbackAttachments,
	deleteFeedbackAttachments,
	createBeforeUpload,
	type FeedbackAttachment
} from "utils/files-management";

interface ProductDetailsFeedbackProps {
	productId: string;
	productName: string;
	installedProductId?: string;
}

const CollapseLabel = ({ productName }: { productName: string }) => {
	return (
		<div className="product-details-feedback-collapse-label">
			<div className="product-details-feedback-collapse-label-title">
				<img src={`${process.env.PUBLIC_URL}/assets/images/icons/feedback-icon.svg`} alt="Feedback Icon" />
				{`Share your feedback on the ${productName}.`}
			</div>
			<div className="product-details-feedback-collapse-label-description">
				Help your team and the device manufacturer by sharing your experience.{" "}
				<b>Your response will remain anonymous.</b>
			</div>
		</div>
	);
};

const ProductDetailsFeedback = ({
	productName,
	installedProductId,
	productId,
	setShouldShowCompletionView,
	parentRef
}: ProductDetailsFeedbackProps & {
	setShouldShowCompletionView: (bool: boolean) => void;
	parentRef: React.RefObject<HTMLButtonElement>;
}) => {
	const [feedback, setFeedback] = useState<string>("");
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState("");
	const [isReviewComplete, setIsReviewComplete] = useState(false);
	const [rating, setRating] = useState<number>(0);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isUploadingReview, setIsUploadingReview] = useState(false);
	const [uploadedAttachments, setUploadedAttachments] = useState<FeedbackAttachment[]>([]);
	const uploadedAttachmentsRef = useRef(uploadedAttachments);

	useEffect(() => {
		uploadedAttachmentsRef.current = uploadedAttachments;
	}, [uploadedAttachments]);

	// This is to ensure that if the user navigates away without submitting, we clean up any uploaded files
	// trick? courtesy of copilot
	useEffect(() => {
		return () => deleteFeedbackAttachments(uploadedAttachmentsRef.current);
	}, []);

	useEffect(() => {
		if (isReviewComplete) {
			setShouldShowCompletionView(true);
		}
	}, [isReviewComplete, setShouldShowCompletionView]);

	const handlePreview = useCallback(
		async (file: UploadFile) => {
			if (!file.url && !file.preview) {
				file.preview = await getBase64(file.originFileObj as FileType);
			}

			setPreviewImage(file.url || (file.preview as string));
			setPreviewOpen(true);
		},
		[setPreviewImage, setPreviewOpen]
	);

	const handleChange: UploadProps["onChange"] = useCallback(
		({ fileList: newFileList }) => {
			// There is some weird behavior with the Upload component
			// where it sets the file upload status to 'error' even if the upload was successful.
			const alteredFileList: UploadFile<any>[] = newFileList.map(file => {
				if (file.status === "error" && file.originFileObj) {
					return {
						...file,
						status: "done" // Set status to 'done' if the file was uploaded successfully
					};
				}
				return file;
			});

			setFileList(alteredFileList);
		},
		[setFileList]
	);

	const [addProductFeedback] = useAddProductFeedbackMutation({
		onCompleted: () => {
			// Since the review is successful, no need to track the uploaded attachments anymore
			setUploadedAttachments([]);
			setIsReviewComplete(true);
		},
		onError: error => {
			console.error("Error adding product feedback:", error);
			alert("There was an error submitting your feedback. Please try again later.");
			setIsUploadingReview(false);
		}
	});

	const handleSubmit = useCallback(async () => {
		if (isUploadingReview || rating === 0) return;
		setIsUploadingReview(true);

		const attachments = await uploadFeedbackAttachments({
			fileList,
			uploadedAttachments: uploadedAttachments,
			bucketName: "product-feedback-attachments",
			fileNamePrefix: productName,
			setUploadedAttachments: setUploadedAttachments
		});

		if (attachments === undefined) {
			setIsUploadingReview(false);
			return;
		}

		addProductFeedback({
			variables: {
				input: {
					installed_product_id: installedProductId,
					product_id: productId,
					rating,
					notes: feedback,
					product_feedback_attachments: {
						data: attachments.map(({ storageRef, uid, ...ua }) => ua)
					}
				}
			}
		});
	}, [isUploadingReview, uploadedAttachments, fileList, productName, installedProductId, productId, rating, feedback]);

	return (
		<div>
			<div className="product-details-feedback-rate-your-experience">
				How would you rate your experience with this device?
				<StarRating onRatingChange={rating => setRating(rating)} />
			</div>

			<div className="product-details-feedback-share-your-experience">
				Share your experience.
				<Input.TextArea
					className="product-details-feedback-wrapper"
					name="product-details-feedback"
					placeholder="Describe your experience with this device. Would you like to share anything with the manufacturer or other users?"
					value={feedback}
					autoComplete="off"
					onChange={e => setFeedback(e.target.value)}
					allowClear
					rows={2}
				/>
			</div>

			<div className="product-details-feedback-attachment">
				Attach your videos or pictures. This could be a video review, a ‘how-to’, or a device improvement idea. (Max: 5)
				<div className="product-details-feedback-upload">
					{previewImage && (
						<Image
							wrapperStyle={{ display: "none" }}
							preview={{
								visible: previewOpen,
								onVisibleChange: visible => setPreviewOpen(visible),
								afterOpenChange: visible => !visible && setPreviewImage("")
							}}
							src={previewImage}
						/>
					)}
					<Upload
						listType="picture-card"
						fileList={fileList}
						onPreview={handlePreview}
						onChange={handleChange}
						accept="image/*,video/*"
						beforeUpload={createBeforeUpload({
							allowedTypePattern: /^image\/|^video\//,
							onInvalidType: () => alert("Only images and videos are allowed.")
						})}
						maxCount={5}
						multiple
					>
						{fileList.length < 5 && (
							<button style={{ border: 0, background: "none" }} type="button">
								<img src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle.svg`} alt="Upload Icon" />
							</button>
						)}
					</Upload>
				</div>
			</div>
			<Button
				className="product-details-feedback-upload-button"
				onClick={handleSubmit}
				disabled={isUploadingReview || fileList.some(file => file.status === "uploading")}
				ref={parentRef}
			>
				Submit
			</Button>

			<LoadingOverlay isLoading={isUploadingReview || fileList.some(file => file.status === "uploading")} />
		</div>
	);
};

const ProductDetailsFeedbackCollapse = (props: ProductDetailsFeedbackProps) => {
	const [shouldShowCompletionView, setShouldShowCompletionView] = useState(false);
	const scrollIntoViewRef = useRef<HTMLButtonElement>(null);

	if (shouldShowCompletionView) {
		return (
			<Card title="Thank you for your feedback!" className="product-details-feedback-complete">
				<span>We appreciate you taking the time to share your experience with this device!</span>
			</Card>
		);
	}

	return (
		<div className="product-details-feedback">
			<Collapse
				items={[
					{
						key: "1",
						label: <CollapseLabel productName={props.productName} />,
						children: (
							<ProductDetailsFeedback
								{...props}
								setShouldShowCompletionView={setShouldShowCompletionView}
								parentRef={scrollIntoViewRef}
							/>
						)
					}
				]}
				onChange={(val: Array<string>) => {
					setTimeout(() => {
						if (val.length && scrollIntoViewRef.current) {
							scrollIntoViewRef?.current?.scrollIntoView({ behavior: "smooth" });
						}
					}, 200);
				}}
				expandIcon={({ isActive }) => <DropdownArrow isActive={isActive} />}
				expandIconPosition="end"
			/>
		</div>
	);
};

export default ProductDetailsFeedbackCollapse;
